import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit2, Trash2, Plus, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";
import RuleItem from "./RuleItem";
import CreateRuleDialog from "./CreateRuleDialog";
import { DocumentViewer } from "./document-viewer";
import { SourceLocation } from "../../services/Playbook";
import { getPlaybookSourceDocument, getPlaybookSourceDocumentFile } from "../../services/Playbook";

export default function PlaybookDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const getUser = useBoundStore((state) => state.getUser);
  const user = getUser();

  // Zustand store
  const selectedPlaybook = useBoundStore((state) => state.selectedPlaybook);
  const isLoading = useBoundStore((state) => state.isLoading);
  const fetchPlaybook = useBoundStore((state) => state.fetchPlaybook);
  const updatePlaybook = useBoundStore((state) => state.updatePlaybook);
  const deletePlaybook = useBoundStore((state) => state.deletePlaybook);

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAgreementType, setEditAgreementType] = useState("");
  const [editIsPrivate, setEditIsPrivate] = useState(true);
  const [createRuleDialogOpen, setCreateRuleDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Source document state
  const [showSourceView, setShowSourceView] = useState(false);
  const [sourceDocumentUrl, setSourceDocumentUrl] = useState<string | null>(null);
  const [sourceDocumentType, setSourceDocumentType] = useState<'docx' | 'xlsx' | 'pdf' | 'txt' | null>(null);
  const [sourceDocumentMetadata, setSourceDocumentMetadata] = useState<any>(null);
  const [loadingSourceDoc, setLoadingSourceDoc] = useState(false);
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const [hoveredSource, setHoveredSource] = useState<SourceLocation | null>(null);
  const [scrollToSource, setScrollToSource] = useState<SourceLocation | null>(null);

  // Fetch playbook on mount
  useEffect(() => {
    if (id) {
      fetchPlaybook(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update local state when playbook changes
  useEffect(() => {
    if (selectedPlaybook) {
      setEditName(selectedPlaybook.name);
      setEditAgreementType(selectedPlaybook.agreementType);
      setEditIsPrivate(selectedPlaybook.isPrivate);

      // Auto-enable source view if playbook has a source document
      if (selectedPlaybook.sourceDocumentS3Key) {
        setShowSourceView(true);
      }
    }
  }, [selectedPlaybook]);

  // Load source document when showSourceView is enabled and we have a source document
  useEffect(() => {
    const loadSourceDocument = async () => {
      if (!showSourceView || !id || !selectedPlaybook?.sourceDocumentS3Key) {
        return;
      }

      try {
        setLoadingSourceDoc(true);

        // Fetch metadata and file in parallel
        const [metadataResponse, fileResponse] = await Promise.all([
          getPlaybookSourceDocument(id),
          getPlaybookSourceDocumentFile(id),
        ]);

        if (metadataResponse.success && metadataResponse.data) {
          setSourceDocumentType(metadataResponse.data.type as 'docx' | 'xlsx' | 'pdf' | 'txt');
          setSourceDocumentMetadata(metadataResponse.data.metadata);
        }

        if (fileResponse.success && fileResponse.data) {
          // Create a blob URL for the file
          const blobUrl = URL.createObjectURL(fileResponse.data);
          setSourceDocumentUrl(blobUrl);
        } else {
          toast.error(t.playbooks.detail.loadSourceFileFailed);
        }
      } catch (error) {
        console.error("Error loading source document:", error);
        toast.error(t.playbooks.detail.loadSourceError);
      } finally {
        setLoadingSourceDoc(false);
      }
    };

    loadSourceDocument();

    // Cleanup blob URL when component unmounts or source changes
    return () => {
      if (sourceDocumentUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(sourceDocumentUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sourceDocumentUrl is set inside this effect, adding it would cause infinite loop
  }, [showSourceView, id, selectedPlaybook?.sourceDocumentS3Key]);

  const canEdit = selectedPlaybook?.userId === user?.id;

  const handleSaveEdit = useCallback(async () => {
    if (!id || !selectedPlaybook) return;

    if (!editName.trim() || !editAgreementType.trim()) {
      toast.error(t.playbooks.detail.nameAndTypeRequired);
      return;
    }

    const updated = await updatePlaybook(id, {
      name: editName.trim(),
      agreementType: editAgreementType.trim(),
      isPrivate: editIsPrivate,
    });

    if (updated) {
      toast.success(t.playbooks.detail.updateSuccess);
      setIsEditing(false);
    } else {
      toast.error(t.playbooks.detail.updateFailed);
    }
  }, [id, selectedPlaybook, editName, editAgreementType, editIsPrivate, updatePlaybook, t]);

  const handleCancelEdit = useCallback(() => {
    if (selectedPlaybook) {
      setEditName(selectedPlaybook.name);
      setEditAgreementType(selectedPlaybook.agreementType);
      setEditIsPrivate(selectedPlaybook.isPrivate);
    }
    setIsEditing(false);
  }, [selectedPlaybook]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return;

    const success = await deletePlaybook(id);
    if (success) {
      toast.success(t.playbooks.detail.deleteSuccess);
      navigate("/playbooks");
    } else {
      toast.error(t.playbooks.detail.deleteFailed);
    }
    setDeleteDialogOpen(false);
  }, [id, deletePlaybook, navigate, t]);

  if (isLoading && !selectedPlaybook) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">{t.playbooks.detail.loading}</p>
      </div>
    );
  }

  if (!selectedPlaybook) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-muted-foreground">{t.playbooks.detail.notFound}</p>
        <Button onClick={() => navigate("/playbooks")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.playbooks.detail.backToPlaybooks}
        </Button>
      </div>
    );
  }

  // Get highlighted sources - prioritize specific hovered source, then fall back to all sources from hovered rule
  const highlightedSources: SourceLocation[] = hoveredSource
    ? [hoveredSource]
    : hoveredRuleId
    ? selectedPlaybook.rules.find((r) => r.id === hoveredRuleId)?.sourceLocations || []
    : [];

  const hasSourceDocument = Boolean(selectedPlaybook.sourceDocumentS3Key);

  return (
    <div className="w-full space-y-4 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/playbooks")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {selectedPlaybook.name}
            </h1>
            <p className="text-muted-foreground">{selectedPlaybook.agreementType}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {hasSourceDocument && (
            <Button
              variant="outline"
              onClick={() => setShowSourceView(!showSourceView)}
            >
              {showSourceView ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  {t.playbooks.detail.hideSource}
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  {t.playbooks.detail.showSource}
                </>
              )}
            </Button>
          )}
          {canEdit && !isEditing && (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Edit2 className="mr-2 h-4 w-4" />
                {t.playbooks.detail.edit}
              </Button>
              <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                {t.playbooks.detail.delete}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Playbook Info */}
      {isEditing && (
        <Card>
          <CardHeader>
            <CardTitle>{t.playbooks.detail.editTitle}</CardTitle>
            <CardDescription>{t.playbooks.detail.editDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t.playbooks.detail.nameLabel}</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t.playbooks.detail.namePlaceholder}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="agreementType">{t.playbooks.detail.agreementTypeLabel}</Label>
              <Input
                id="agreementType"
                value={editAgreementType}
                onChange={(e) => setEditAgreementType(e.target.value)}
                placeholder={t.playbooks.detail.agreementTypePlaceholder}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="isPrivate">{t.playbooks.detail.privateLabel}</Label>
                <p className="text-sm text-muted-foreground">
                  {editIsPrivate
                    ? t.playbooks.detail.privateOnlyYou
                    : t.playbooks.detail.privateEveryone}
                </p>
              </div>
              <Switch id="isPrivate" checked={editIsPrivate} onCheckedChange={setEditIsPrivate} />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveEdit} disabled={isLoading}>
                {t.playbooks.detail.saveChanges}
              </Button>
              <Button variant="outline" onClick={handleCancelEdit} disabled={isLoading}>
                {t.playbooks.detail.cancel}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules and Source Document - Split View */}
      {showSourceView && hasSourceDocument ? (
        <ResizablePanelGroup orientation="horizontal" className="min-h-[600px] rounded-lg">
          {/* Rules Panel */}
          <ResizablePanel defaultSize="50%" minSize="25%">
            <Card className="h-full overflow-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t.playbooks.detail.rulesTitle}</CardTitle>
                    <CardDescription>{t.playbooks.detail.rulesDescription}</CardDescription>
                  </div>
                  {canEdit && (
                    <Button onClick={() => setCreateRuleDialogOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      {t.playbooks.detail.addRule}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedPlaybook.rules.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t.playbooks.detail.noRules}</p>
                    {canEdit && <p className="mt-2">{t.playbooks.detail.addFirstRule}</p>}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedPlaybook.rules.map((rule) => (
                      <RuleItem
                        key={rule.id}
                        rule={rule}
                        playbookId={selectedPlaybook.id}
                        canEdit={canEdit}
                        onHover={(isHovered) => setHoveredRuleId(isHovered ? rule.id : null)}
                        isHovered={hoveredRuleId === rule.id}
                        onSourceHover={(source) => setHoveredSource(source)}
                        onSourceClick={(source) => setScrollToSource(source)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Source Document Panel */}
          <ResizablePanel defaultSize="50%" minSize="25%">
            <Card className="h-full overflow-hidden ml-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t.playbooks.detail.sourceDocumentTitle}</CardTitle>
                    <CardDescription>
                      {t.playbooks.detail.sourceDocumentDescription(selectedPlaybook.sourceDocumentType?.toUpperCase() ?? "")}
                      {sourceDocumentMetadata?.pageCount && t.playbooks.detail.sourceDocumentPages(sourceDocumentMetadata.pageCount)}
                      {sourceDocumentMetadata?.sheetNames && t.playbooks.detail.sourceDocumentSheets(sourceDocumentMetadata.sheetNames.length)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-[calc(100%-70px)] overflow-hidden">
                {loadingSourceDoc ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">{t.playbooks.detail.loadingSourceDocument}</p>
                  </div>
                ) : sourceDocumentUrl && sourceDocumentType ? (
                  <DocumentViewer
                    fileUrl={sourceDocumentUrl}
                    fileType={sourceDocumentType}
                    metadata={sourceDocumentMetadata}
                    highlightedSources={highlightedSources}
                    activeRuleId={hoveredRuleId || undefined}
                    scrollToSource={scrollToSource}
                    onScrollComplete={() => setScrollToSource(null)}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">{t.playbooks.detail.noSourceDocument}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{t.playbooks.detail.rulesTitle}</CardTitle>
                <CardDescription>{t.playbooks.detail.rulesDescription}</CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => setCreateRuleDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t.playbooks.detail.addRule}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {selectedPlaybook.rules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>{t.playbooks.detail.noRules}</p>
                {canEdit && <p className="mt-2">{t.playbooks.detail.addFirstRule}</p>}
              </div>
            ) : (
              <div className="space-y-4">
                {selectedPlaybook.rules.map((rule) => (
                  <RuleItem
                    key={rule.id}
                    rule={rule}
                    playbookId={selectedPlaybook.id}
                    canEdit={canEdit}
                    onHover={(isHovered) => setHoveredRuleId(isHovered ? rule.id : null)}
                    isHovered={hoveredRuleId === rule.id}
                    onSourceHover={(source) => setHoveredSource(source)}
                    onSourceClick={(source) => setScrollToSource(source)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Rule Dialog */}
      {canEdit && (
        <CreateRuleDialog
          open={createRuleDialogOpen}
          onOpenChange={setCreateRuleDialogOpen}
          playbookId={selectedPlaybook.id}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.playbooks.deleteDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.playbooks.deleteDialog.description(selectedPlaybook.name)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.playbooks.deleteDialog.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t.playbooks.deleteDialog.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
